import type {
  ContactGroupSummary,
  ContactSortMode,
  ThreadItemForGrouping,
} from './types'

export type { ContactSortMode } from './types'

export function maxIsoTimestamp(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  if (!a) return b ?? null
  if (!b) return a
  return new Date(a) > new Date(b) ? a : b
}

function getThreadContactActivity(thread: ThreadItemForGrouping) {
  const contactParticipant = thread.participants.find((p) => p.kind === 'CONTACT')
  return contactParticipant?.contactActivity ?? null
}

function isScheduledStatusActive(thread: ThreadItemForGrouping): boolean {
  return (
    !!thread.hasScheduledMessage &&
    !!thread.nextScheduledAt &&
    !thread.hasActiveWorkflow &&
    !thread.hasActiveAgent &&
    (thread.pendingApprovalCount ?? 0) === 0
  )
}

export function groupThreadsByContact<T extends ThreadItemForGrouping>(
  threads: T[],
  sortMode: ContactSortMode = 'last_activity',
): Array<ContactGroupSummary & { threads: T[] }> {
  const groupMap = new Map<string, ContactGroupSummary & { threads: T[] }>()

  for (const thread of threads) {
    const contactParticipant = thread.participants.find(
      (p) => p.kind === 'CONTACT',
    )
    if (!contactParticipant) continue

    const groupKey = contactParticipant.contactId || contactParticipant.id
    const contactActivity = getThreadContactActivity(thread)
    const existing = groupMap.get(groupKey)

    if (existing) {
      existing.threads.push(thread)
      existing.totalUnreadCount += thread.unreadCount
      if (
        thread.lastMessageAt &&
        (!existing.lastMessageAt ||
          new Date(thread.lastMessageAt) > new Date(existing.lastMessageAt))
      ) {
        existing.lastMessageAt = thread.lastMessageAt
        if (!contactActivity?.lastMessage) {
          existing.lastMessagePreview = thread.lastMessagePreview
        }
      }
      if (contactActivity?.lastMessage) {
        existing.lastMessagePreview = contactActivity.lastMessage
      }
      existing.lastContactedAt = maxIsoTimestamp(
        existing.lastContactedAt,
        contactActivity?.lastContactedAt,
      )
      existing.lastActivityAt = maxIsoTimestamp(
        existing.lastActivityAt,
        maxIsoTimestamp(
          contactActivity?.lastActivityAt,
          thread.lastActivityAt ?? null,
        ),
      )
      if (isScheduledStatusActive(thread) && thread.nextScheduledAt) {
        if (
          !existing.nextScheduledAt ||
          new Date(thread.nextScheduledAt) < new Date(existing.nextScheduledAt)
        ) {
          existing.nextScheduledAt = thread.nextScheduledAt
        }
      }
    } else {
      const displaySortAt =
        sortMode === 'last_contact'
          ? contactActivity?.lastContactedAt ?? null
          : maxIsoTimestamp(
              contactActivity?.lastActivityAt,
              thread.lastActivityAt ?? null,
            )

      groupMap.set(groupKey, {
        contactId: groupKey,
        displayName: contactParticipant.displayName,
        avatarUrl: contactParticipant.avatarUrl,
        imageSeed: contactParticipant.imageSeed ?? null,
        lastMessageAt: thread.lastMessageAt,
        lastMessagePreview:
          contactActivity?.lastMessage ?? thread.lastMessagePreview,
        lastContactedAt: contactActivity?.lastContactedAt ?? null,
        lastActivityAt: maxIsoTimestamp(
          contactActivity?.lastActivityAt,
          thread.lastActivityAt ?? null,
        ),
        displayTimeAt: displaySortAt ?? thread.lastMessageAt,
        totalUnreadCount: thread.unreadCount,
        threads: [thread],
        nextScheduledAt:
          isScheduledStatusActive(thread) && thread.nextScheduledAt
            ? thread.nextScheduledAt
            : null,
      })
    }
  }

  for (const group of groupMap.values()) {
    group.threads.sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0
      return bTime - aTime
    })

    group.displayTimeAt =
      sortMode === 'last_contact'
        ? group.lastContactedAt ?? group.lastMessageAt
        : group.lastActivityAt ?? group.lastMessageAt
  }

  return Array.from(groupMap.values()).sort((a, b) => {
    const aTime = a.displayTimeAt ? new Date(a.displayTimeAt).getTime() : 0
    const bTime = b.displayTimeAt ? new Date(b.displayTimeAt).getTime() : 0
    return bTime - aTime
  })
}
